import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  App,
  Button,
  Card,
  Form,
  InputNumber,
  Modal,
  Progress,
  Select,
  Space,
  Table,
  Typography,
} from "antd";
import { useAuth } from "../../../context/AuthContext";
import { useThemeStyles } from "../../../context/ThemeContext";
import {
  getAdminGroupsBudgetSummary,
  updateAdminGroupBudget,
} from "../../../services/api";
import { formatUsd } from "../utils";
import GroupBudgetUtilizationChart from "../components/GroupBudgetUtilizationChart";

const { Text } = Typography;

export default function AdminGroupBudgetsPage() {
  const { token, isSuperAdmin } = useAuth();
  const themeStyles = useThemeStyles();
  const { message } = App.useApp();
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [groupModalOpen, setGroupModalOpen] = useState(false);
  const [activeGroup, setActiveGroup] = useState(null);
  const [groupSubmitting, setGroupSubmitting] = useState(false);
  const [groupForm] = Form.useForm();

  const load = useCallback(async () => {
    if (!token || !isSuperAdmin) return;
    setLoading(true);
    try {
      const res = await getAdminGroupsBudgetSummary(token, days);
      setSummary(res?.data || null);
    } catch (e) {
      message.error(e?.response?.data?.detail || "Failed to load group budgets");
    } finally {
      setLoading(false);
    }
  }, [token, isSuperAdmin, days, message]);

  useEffect(() => {
    load();
  }, [load]);

  const groups = summary?.groups || [];

  const openGroupBudgetModal = (row) => {
    setActiveGroup(row);
    groupForm.setFieldsValue({
      monthly_limit_usd: row?.budget?.monthly_limit_usd ?? null,
    });
    setGroupModalOpen(true);
  };

  const submitGroupBudget = async () => {
    const v = await groupForm.validateFields();
    if (!activeGroup?.group_id) return;
    setGroupSubmitting(true);
    try {
      const existing = activeGroup?.budget || {};
      await updateAdminGroupBudget(token, activeGroup.group_id, {
        monthly_limit_usd:
          v.monthly_limit_usd === undefined || v.monthly_limit_usd === null
            ? null
            : Number(v.monthly_limit_usd),
        alert_threshold_pct: Number(existing.alert_threshold_pct ?? 80),
        block_when_exceeded: !!existing.block_when_exceeded,
      });
      message.success("Group budget updated");
      setGroupModalOpen(false);
      load();
    } catch (e) {
      message.error(e?.response?.data?.detail || "Failed to update group budget");
    } finally {
      setGroupSubmitting(false);
    }
  };

  const projectColumns = useMemo(
    () => [
      {
        title: "Project",
        dataIndex: "name",
        key: "name",
        render: (name, row) => name || row.project_id,
      },
      {
        title: "Project cap",
        dataIndex: "monthly_limit_usd",
        key: "cap",
        width: 110,
        render: (v) => (v == null ? "—" : formatUsd(v)),
      },
      {
        title: "Spend",
        dataIndex: "spend_usd",
        key: "spend",
        width: 100,
        render: (v) => formatUsd(v),
      },
      {
        title: "Utilization",
        key: "util",
        width: 180,
        render: (_, row) => {
          const pct = row.utilization_pct;
          if (pct == null) return <Text type="secondary">—</Text>;
          const p = Math.min(100, Number(pct));
          const status = p >= 100 ? "exception" : p >= 80 ? "active" : "normal";
          return <Progress percent={Math.round(p * 10) / 10} size="small" status={status} />;
        },
      },
    ],
    [],
  );

  const columns = useMemo(
    () => [
      {
        title: "Group",
        dataIndex: "group_name",
        key: "group_name",
        render: (name, row) => (
          <Text strong style={{ color: themeStyles.textPrimary }}>
            {name || row.group_id}
          </Text>
        ),
      },
      {
        title: "Projects",
        dataIndex: "project_count",
        key: "project_count",
        width: 90,
      },
      {
        title: `Spend (${days}d)`,
        dataIndex: "spend_usd",
        key: "spend_usd",
        width: 110,
        render: (v) => formatUsd(v),
      },
      {
        title: "Group budget",
        key: "limit",
        width: 110,
        render: (_, row) => {
          const lim = row?.budget?.monthly_limit_usd;
          return lim == null ? "—" : formatUsd(lim);
        },
      },
      {
        title: "Utilization",
        key: "util",
        width: 180,
        render: (_, row) => {
          const pct = row.utilization_pct;
          if (pct == null) return <Text type="secondary">—</Text>;
          const p = Number(pct);
          const status = p >= 100 ? "exception" : p >= 80 ? "active" : "normal";
          return <Progress percent={Math.min(100, p)} size="small" status={status} />;
        },
      },
      {
        title: "Allocated by admin",
        dataIndex: "total_allocated_to_projects_usd",
        key: "allocated",
        width: 130,
        render: (v) => (v == null ? "—" : formatUsd(v)),
      },
      {
        title: "Actions",
        key: "actions",
        width: 120,
        render: (_, row) => (
          <Button type="link" size="small" onClick={() => openGroupBudgetModal(row)}>
            Set group budget
          </Button>
        ),
      },
    ],
    [days, themeStyles.textPrimary],
  );

  if (!isSuperAdmin) {
    return (
      <Card>
        <Text type="secondary">Super admin access required for group budgets.</Text>
      </Card>
    );
  }

  return (
    <>
      <Space direction="vertical" style={{ width: "100%" }} size={12}>
        <Text type="secondary">
          Assign budget caps to groups only. Group admins allocate project and user budgets within your
          group cap.
        </Text>
        <Space wrap>
          <Text type="secondary">Rolling window:</Text>
          <Select
            value={days}
            onChange={setDays}
            style={{ width: 160 }}
            options={[
              { label: "Last 7 days", value: 7 },
              { label: "Last 30 days", value: 30 },
              { label: "Last 90 days", value: 90 },
            ]}
          />
          <Text style={{ color: themeStyles.textPrimary, fontWeight: 600 }}>
            Total group spend: {formatUsd(summary?.total_spend_usd)}
          </Text>
        </Space>

        <GroupBudgetUtilizationChart groups={groups} days={days} loading={loading} />

        <Table
          rowKey={(r) => r.group_id}
          loading={loading}
          columns={columns}
          dataSource={groups}
          pagination={{ pageSize: 10 }}
          expandable={{
            expandedRowRender: (record) => (
              <Table
                size="small"
                rowKey="project_id"
                pagination={false}
                dataSource={record.projects || []}
                columns={projectColumns}
              />
            ),
            rowExpandable: (record) => (record.projects || []).length > 0,
          }}
        />
      </Space>

      <Modal
        title={activeGroup ? `Group budget — ${activeGroup.group_name || activeGroup.group_id}` : "Group budget"}
        open={groupModalOpen}
        onCancel={() => setGroupModalOpen(false)}
        onOk={submitGroupBudget}
        confirmLoading={groupSubmitting}
        destroyOnClose
      >
        <Form form={groupForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="monthly_limit_usd"
            label="Group budget (USD)"
            extra="Total cap for this group. Group admins split this across projects. Cannot be less than project caps already set by admins."
          >
            <InputNumber min={0} precision={6} step={0.01} style={{ width: "100%" }} placeholder="No cap" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
